<template>
  <section class="py-8 sm:py-10">
    <div class="max-w-screen-lg mx-auto px-4">

      <div class="flex items-end justify-between mb-5 gap-4">
        <div>
          <span class="inline-block text-[0.68rem] font-bold tracking-[0.1em] uppercase px-3 py-1 rounded-full mb-2 bg-[#FFF3B0] text-[#7a5f00]">Aanbiedingen</span>
          <h2 class="text-[1.25rem] font-black text-[#111] leading-tight">Onze aanbiedingen</h2>
        </div>
        <router-link to="/acties" class="text-[0.8125rem] font-semibold text-[#111] no-underline hover:text-[#b8860b] flex items-center gap-1 transition-colors shrink-0 pb-0.5">
          Alle aanbiedingen <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>

      <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div v-for="i in 5" :key="i" class="bg-[#f5f5f5] rounded-[10px] h-[260px] animate-pulse"></div>
      </div>

      <div v-else-if="products.length" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <router-link
          v-for="p in products"
          :key="p.id"
          :to="`/product/${p.slug}`"
          class="product-card group no-underline"
        >
          <!-- Afbeelding -->
          <div class="relative bg-[#f8f8f8] rounded-[8px] overflow-hidden mb-3 aspect-square flex items-center justify-center">
            <img
              v-if="p.images && p.images[0]"
              :src="p.images[0].src"
              :alt="p.name"
              class="max-h-full max-w-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            >
            <!-- Korting badge -->
            <span v-if="p.on_sale && discountPct(p)" class="absolute top-2 left-2 bg-[#ea580c] text-white text-[0.65rem] font-bold px-2 py-0.5 rounded-full">
              -{{ discountPct(p) }}%
            </span>
            <!-- Merk badge -->
            <span v-if="brandName(p)" class="absolute top-2 right-2 bg-white/90 text-[#555] text-[0.6rem] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide border border-[#e8e8e8]">
              {{ brandName(p) }}
            </span>
          </div>

          <!-- Info -->
          <div class="px-1">
            <p class="text-[0.65rem] text-[#888] mb-0.5 uppercase tracking-wide font-semibold truncate">{{ categoryName(p) }}</p>
            <h3 class="text-[0.85rem] font-semibold text-[#111] leading-snug mb-1.5 line-clamp-2">{{ p.name }}</h3>
            <!-- Feature bullets -->
            <ul v-if="bullets(p).length" class="mb-2 flex flex-col gap-0.5 list-none p-0 m-0">
              <li v-for="b in bullets(p)" :key="b" class="flex items-start gap-1.5 text-[0.72rem] text-[#444] leading-snug">
                <i class="bi bi-check2 text-[#FFD500] shrink-0 mt-[1px]" aria-hidden="true"></i>
                {{ b }}
              </li>
            </ul>
            <div class="flex items-baseline gap-1.5 flex-wrap">
              <span v-if="p.on_sale && p.regular_price" class="text-[#aaa] line-through text-[0.75rem]">{{ fmt(p.regular_price) }}</span>
              <span class="text-[1rem] font-black text-[#111]">{{ fmt(p.price) }}</span>
              <span class="text-[0.65rem] text-[#888]">excl. btw</span>
            </div>
            <p class="mt-1.5 text-[0.72rem] font-semibold" :class="p.stock_status === 'instock' ? 'text-[#16a34a]' : 'text-[#888]'">
              <i class="bi" :class="p.stock_status === 'instock' ? 'bi-check-circle-fill' : 'bi-clock'"></i>
              {{ p.stock_status === 'instock' ? 'Op voorraad' : 'Uitverkocht' }}
            </p>
          </div>

          <!-- Knop -->
          <div class="mt-3 px-1">
            <span class="block w-full text-center bg-[#FFD500] text-[#111] font-semibold text-[0.8rem] py-2 rounded-[7px] hover:bg-[#ffb800] transition-colors">
              Bekijk product
            </span>
          </div>
        </router-link>
      </div>

      <p v-else class="text-[#888] text-sm">Geen aanbiedingen gevonden.</p>

    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { fetchProductsAll } from '@/composables/useProductsAll.js'

const products = ref([])
const loading  = ref(true)

const fmt = (price) => price
  ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(parseFloat(price))
  : ''

const discountPct = (p) => {
  if (!p.regular_price || !p.price) return 0
  return Math.round((1 - parseFloat(p.price) / parseFloat(p.regular_price)) * 100)
}

const brandName = (p) => {
  const tag = (p.tags || []).find(t => t.name)
  return tag?.name || null
}

const categoryName = (p) => (p.categories || [])[0]?.name || ''

const bullets = (p) => {
  const html = p.short_description || ''
  const matches = html.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || []
  return matches
    .slice(0, 3)
    .map(li => li.replace(/<[^>]+>/g, '').trim())
    .filter(Boolean)
}

onMounted(async () => {
  try {
    const res = await fetch('/_products-aanbiedingen')
    products.value = res.ok ? await res.json() : []
  } catch (e) {
    console.error('HomeAanbiedingen fetch error:', e)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.product-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1.5px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
}
.product-card:hover {
  border-color: #FFD500;
  box-shadow: 0 4px 16px rgba(17,17,17,0.07);
  transform: translateY(-2px);
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
